/**
 * Placeholder image for listings that were created without photos
 * (RESPONDER_BADGE_WANTLIST_CLOSET_PLAN.md §4, 2026-10-18).
 *
 * A listing with no photos carries `placeholderImage`:
 *   { source: 'reverb' | 'sketch', url, link, slug }
 * 'reverb' is Reverb's own catalog product photo, hotlinked (never copied into
 * our Storage, per the API terms' caching clause) with `link` pointing at the
 * Reverb product page for the required link back. 'sketch' is our own generic
 * line drawing, the floor when Reverb has no entry, the member typed an unknown
 * pedal, or the hotlink fails to load.
 *
 * The listing's own `images` map stays the source of truth for "has photos":
 * the moment a real top photo exists, the placeholder is ignored everywhere.
 */

// Generic stand-in for a listing with no Reverb catalog match: a bare three-knob
// enclosure photographed on white (Jason, 2026-10-19), so it sits beside Reverb's
// catalog product shots without looking like a different kind of image. Served
// as an 800px JPEG derived from public/images/placeholder-pedal.png. The name
// SKETCH_* is kept so the call sites did not need to change; it means "ours,
// not Reverb's".
export const SKETCH_PLACEHOLDER_URL = '/images/placeholder-pedal.jpg';

export const LISTING_IMAGE_KEYS = ['top', 'back', 'upper', 'lower', 'detail1', 'detail2', 'detail3', 'detail4'];

/** True when the listing has no real photos at all. */
export function hasNoPhotos(pedal) {
  const images = pedal && pedal.images && typeof pedal.images === 'object' ? pedal.images : {};
  return !LISTING_IMAGE_KEYS.some((k) => typeof images[k] === 'string' && images[k].trim() !== '');
}

/** True when the listing should render with a placeholder and the "Photos coming" tag. */
export function isPlaceholderListing(pedal) {
  return !!pedal && hasNoPhotos(pedal);
}

/**
 * Build the `placeholderImage` field for a new photo-less listing from the
 * Reverb catalog facts the typeahead handed us (may be null).
 */
export function buildPlaceholderImage(reverbCatalog) {
  if (reverbCatalog && typeof reverbCatalog.imageUrl === 'string' && reverbCatalog.imageUrl.trim()) {
    return {
      source: 'reverb',
      url: reverbCatalog.imageUrl,
      link: typeof reverbCatalog.webLink === 'string' ? reverbCatalog.webLink : null,
      slug: typeof reverbCatalog.slug === 'string' ? reverbCatalog.slug : null,
    };
  }
  return { source: 'sketch', url: SKETCH_PLACEHOLDER_URL, link: null, slug: null };
}

/** The placeholder to show for a listing: its stored one, or the sketch. */
export function placeholderFor(pedal) {
  const p = pedal && pedal.placeholderImage;
  // Reverb entries keep their stored URL. Our own stand-in always resolves to
  // the CURRENT file, so swapping the artwork never leaves old listings behind.
  if (p && p.source === 'reverb' && typeof p.url === 'string' && p.url.trim()) {
    return { source: 'reverb', url: p.url, link: p.link || null };
  }
  return { source: 'sketch', url: SKETCH_PLACEHOLDER_URL, link: null };
}

/**
 * Ordered image URLs for a card carousel. Real photos when there are any,
 * otherwise exactly one entry: the placeholder.
 */
export function listingImageUrls(pedal) {
  const images = pedal && pedal.images && typeof pedal.images === 'object' ? pedal.images : {};
  const urls = LISTING_IMAGE_KEYS.map((k) => images[k]).filter((u) => typeof u === 'string' && u.trim() !== '');
  return urls.length > 0 ? urls : [placeholderFor(pedal).url];
}
